import React from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { FamilyProvider } from "@/contexts/FamilyContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { NotificationPrompt } from "@/components/notifications";
import { VideoCall } from "@/components/chat";
import { MainLayout } from "@/components/layout";
import { 
  HomePage, 
  LoginPage, 
  RegisterPage, 
  ForgotPasswordPage,
  DoctorsPage,
  DoctorDetailPage,
  SpecialtiesPage,
  AppointmentsPage,
  MedicalRecordsPage,
  ProfilePage,
  FavoritesPage,
  NotificationsPage,
  BookingCheckoutPage,
  RateDoctorPage,
  MessagesPage,
  WalletPage,
  SettingsPage,
  HelpPage,
  ClinicDetailPage,
  FamilyMembersPage,
  AboutPage,
  ContactPage,
  PrivacyPage,
  TermsPage
} from "@/pages";
import {
  DoctorDashboard,
  DoctorAppointments,
  DoctorSchedule,
  DoctorPatients,
  DoctorReviews,
  PatientDetailPage,
  DoctorProfile,
  DoctorWallet
} from "@/pages/doctor";
import {
  ClinicDashboard,
  ClinicPatients,
  ClinicStaff,
  ClinicBilling,
  ClinicReports,
  ClinicSettings
} from "@/pages/clinic";
import {
  PharmacyListPage,
  MedicinesPage,
  PharmacyCartPage,
  PharmacyCheckoutPage,
  PharmacyOrdersPage,
  MedicineDetailPage
} from "@/pages/pharmacy";
import { PharmacyCartProvider } from "@/contexts/PharmacyCartContext";
import { Toaster } from "@/components/ui/sonner";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FavoritesProvider>
          <NotificationProvider>
          <ChatProvider>
          <FamilyProvider>
            <PharmacyCartProvider>
              <BrowserRouter>
              <Routes>
              {/* Public routes with MainLayout */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/doctors" element={<DoctorsPage />} />
                <Route path="/doctors/:id" element={<DoctorDetailPage />} />
                <Route path="/doctors/:id/book" element={<DoctorDetailPage />} />
                <Route path="/specialties" element={<SpecialtiesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                
                {/* Patient Features */}
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/checkout/:doctorId" element={<BookingCheckoutPage />} />
                <Route path="/rate/:doctorId" element={<RateDoctorPage />} />
                <Route path="/family" element={<FamilyMembersPage />} />
                
                {/* Protected routes (auth handled inside components) */}
                <Route path="/appointments" element={<AppointmentsPage />} />
                <Route path="/medical-records" element={<MedicalRecordsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/wallet" element={<WalletPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/clinics/:id" element={<ClinicDetailPage />} />
                
                {/* Doctor Portal Routes */}
                <Route path="/doctor/dashboard" element={<DoctorDashboard />} />
                <Route path="/doctor/appointments" element={<DoctorAppointments />} />
                <Route path="/doctor/schedule" element={<DoctorSchedule />} />
                <Route path="/doctor/patients" element={<DoctorPatients />} />
                <Route path="/doctor/patients/:id" element={<PatientDetailPage />} />
                <Route path="/doctor/reviews" element={<DoctorReviews />} />
                <Route path="/doctor/profile" element={<DoctorProfile />} />
                <Route path="/doctor/wallet" element={<DoctorWallet />} />
                
                {/* Clinic Management Dashboard Routes */}
                <Route path="/clinic/dashboard" element={<ClinicDashboard />} />
                <Route path="/clinic/patients" element={<ClinicPatients />} />
                <Route path="/clinic/patients/:id" element={<ClinicPatients />} />
                <Route path="/clinic/staff" element={<ClinicStaff />} />
                <Route path="/clinic/billing" element={<ClinicBilling />} />
                <Route path="/clinic/reports" element={<ClinicReports />} />
                <Route path="/clinic/settings" element={<ClinicSettings />} />
                
                {/* Pharmacy Routes */}
                <Route path="/pharmacy" element={<PharmacyListPage />} />
                <Route path="/pharmacy/medicines" element={<MedicinesPage />} />
                <Route path="/pharmacy/:id" element={<MedicinesPage />} />
                <Route path="/pharmacy/medicine/:id" element={<MedicineDetailPage />} />
                <Route path="/pharmacy/cart" element={<PharmacyCartPage />} />
                <Route path="/pharmacy/checkout" element={<PharmacyCheckoutPage />} />
                <Route path="/pharmacy/orders" element={<PharmacyOrdersPage />} />
              </Route>
              
              {/* Auth routes without MainLayout */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            </Routes>
            <NotificationPrompt />
            <VideoCall />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
          </PharmacyCartProvider>
        </FamilyProvider>
        </ChatProvider>
      </NotificationProvider>
      </FavoritesProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
